import { useCallback, useEffect, useMemo, useState } from "react";
import ProfileAnalysisService from "../../../../../../../service/ProfileAnalysisService";

const profileAnalysisService = new ProfileAnalysisService();

const isSuccessCode = (code) => typeof code === "number" && code >= 200000 && code < 300000;

const getEmptyFormData = () => ({
  meta: {
    id: null,
    consultant: null,
    date: null,
    status: "DRAFT",
    created_at: null,
    updated_at: null,
    consent: false,
  },
  personal_info: {
    first_name: "",
    last_name: "",
    address: "",
    nationality: "",
    birth_place: "",
    birth_date: "",
    phone: {
      country_code: "",
      number: "",
    },
    email: "",
    employment: "",
    marital_status: "",
    education_level: "",
    household_members: null,
  },
  goals_and_needs: {
    objectives: "",
    needs: "",
  },
});

const normalizeApiPayload = (raw) => {
  if (!raw) return null;
  if (Array.isArray(raw)) {
    if (!raw.length) return null;
    return normalizeApiPayload(raw[0]);
  }
  if (raw.profile_analysis) return normalizeApiPayload(raw.profile_analysis);
  if (raw.result) return normalizeApiPayload(raw.result);
  if (raw.personal_info || raw.goals_and_needs || raw.meta) return raw;
  return null;
};

const getPayloadId = (payload) =>
  payload?.meta?.id || payload?.id || payload?._id || payload?.profile_analysis_id || null;

const buildError = (messageCode) => {
  const error = new Error("Backend returned an error");
  error.backendCode = messageCode;
  return error;
};

const clampText = (value) => (value || "").slice(0, 200);

const buildNormalizedPayload = (inputData) => {
  const data = inputData || getEmptyFormData();
  return {
    meta: {
      id: data?.meta?.id || null,
      consultant: data?.meta?.consultant || null,
      date: data?.meta?.date || null,
      status: data?.meta?.status || "DRAFT",
      created_at: data?.meta?.created_at || null,
      updated_at: data?.meta?.updated_at || null,
      consent: !!data?.meta?.consent,
    },
    personal_info: {
      first_name: data?.personal_info?.first_name || "",
      last_name: data?.personal_info?.last_name || "",
      address: data?.personal_info?.address || "",
      nationality: data?.personal_info?.nationality || "",
      birth_place: data?.personal_info?.birth_place || "",
      birth_date: data?.personal_info?.birth_date || "",
      phone: {
        country_code: data?.personal_info?.phone?.country_code || "",
        number: data?.personal_info?.phone?.number || "",
      },
      email: data?.personal_info?.email || "",
      employment: data?.personal_info?.employment || "",
      marital_status: data?.personal_info?.marital_status || "",
      education_level: data?.personal_info?.education_level || "",
      household_members:
        data?.personal_info?.household_members === 0 ||
        typeof data?.personal_info?.household_members === "number"
          ? data.personal_info.household_members
          : null,
    },
    goals_and_needs: {
      objectives: clampText(data?.goals_and_needs?.objectives),
      needs: clampText(data?.goals_and_needs?.needs),
    },
  };
};

const useProfileAnalysisForm = () => {
  const [formData, setFormData] = useState(getEmptyFormData);
  const [analysisId, setAnalysisId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadAnalysis = useCallback(async () => {
    const token = localStorage.getItem("__extraconomy_token");
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const allRes = await profileAnalysisService.getAllProfileAnalyses(token);
      if (allRes?.messageCode && !isSuccessCode(allRes.messageCode)) {
        throw buildError(allRes.messageCode);
      }
      const basePayload = normalizeApiPayload(allRes);
      if (!basePayload) {
        setFormData(getEmptyFormData());
        setAnalysisId(null);
        return;
      }
      const foundId = getPayloadId(basePayload);
      let finalPayload = basePayload;
      if (foundId) {
        const byIdRes = await profileAnalysisService.getProfileAnalysisById(token, foundId);
        if (byIdRes?.messageCode && !isSuccessCode(byIdRes.messageCode)) {
          throw buildError(byIdRes.messageCode);
        }
        finalPayload = normalizeApiPayload(byIdRes) || basePayload;
      }
      const normalized = buildNormalizedPayload(finalPayload);
      const normalizedId = getPayloadId(finalPayload) || foundId;
      normalized.meta.id = normalizedId;
      setFormData(normalized);
      setAnalysisId(normalizedId);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAnalysis().catch(() => {
      setLoading(false);
    });
  }, [loadAnalysis]);

  const submit = useCallback(
    async (values, { finalize = false } = {}) => {
      const token = localStorage.getItem("__extraconomy_token");
      if (!token) throw buildError(401000);
      setSaving(true);
      try {
        const merged = buildNormalizedPayload({
          ...formData,
          ...values,
          meta: {
            ...formData.meta,
            ...(values?.meta || {}),
            status: finalize ? "COMPLETED" : "DRAFT",
            consent: !!values?.meta?.consent,
          },
        });
        let res;
        let currentId = analysisId;
        if (!currentId) {
          res = await profileAnalysisService.createProfileAnalysis(token, merged);
          if (res?.messageCode && !isSuccessCode(res.messageCode)) {
            throw buildError(res.messageCode);
          }
          const createdPayload = normalizeApiPayload(res);
          currentId = getPayloadId(createdPayload);
          if (!currentId) {
            const allRes = await profileAnalysisService.getAllProfileAnalyses(token);
            if (allRes?.messageCode && !isSuccessCode(allRes.messageCode)) {
              throw buildError(allRes.messageCode);
            }
            const loadedPayload = normalizeApiPayload(allRes);
            currentId = getPayloadId(loadedPayload);
          }
          if (!currentId) {
            throw buildError(500000);
          }
          setAnalysisId(currentId);
        }
        res = await profileAnalysisService.updateProfileAnalysis(token, currentId, merged);
        if (res?.messageCode && !isSuccessCode(res.messageCode)) {
          throw buildError(res.messageCode);
        }
        const savedPayload = normalizeApiPayload(res) || merged;
        const next = buildNormalizedPayload(savedPayload);
        next.meta.id = getPayloadId(savedPayload) || currentId;
        next.meta.status = merged.meta.status;
        next.meta.consent = merged.meta.consent;
        setFormData(next);
        return next;
      } catch (error) {
        if (error?.backendCode) throw error;
        throw buildError(error?.response?.data?.messageCode || 500000);
      } finally {
        setSaving(false);
      }
    },
    [analysisId, formData],
  );

  const viewModel = useMemo(
    () => ({
      formData,
      loading,
      saving,
      submit,
      reload: loadAnalysis,
    }),
    [formData, loading, saving, submit, loadAnalysis],
  );

  return viewModel;
};

export default useProfileAnalysisForm;
